import { escapeHtml } from './utils.js';

const FALLBACK_ICONS = {
  collector: '🧩',
  trader: '🎛️',
  influencer: '🖼️',
  breeder: '🕳️'
};

const FALLBACK_NAMES = {
  collector: 'Фрагментатор',
  trader: 'Аудио-демон',
  influencer: 'Искажатель изображения',
  breeder: 'Пустотный протокол'
};

const STAT_LABELS = {
  discovery: 'Поиск фрагментов',
  appraisal: 'Анализ аномалий',
  networking: 'Сеть заражения',
  negotiation: 'Резонанс',
  market_analysis: 'Спектральный анализ',
  risk_management: 'Контроль перегрузки',
  charisma: 'Глитч-харизма',
  content_creation: 'Фрактальный сигнал',
  community_impact: 'Вирусное влияние',
  genetics: 'Патчинг кода',
  care: 'Поддержка ядра',
  evolution: 'Эволюция'
};

export function createProfessionsService({ safeFetch, getAuthHeaders, getApiUrl, cacheTtl = 60 * 60 * 1000 }) {
  let cache = null;
  let cacheTimestamp = 0;

  function getFromCache(id) {
    if (!cache || !id) return null;
    return cache[id] || null;
  }

  async function fetchProfessionsData(force = false) {
    const now = Date.now();
    if (!force && cache && now - cacheTimestamp < cacheTtl) {
      return cache;
    }

    const apiUrl = typeof getApiUrl === 'function' ? getApiUrl() : getApiUrl;
    const result = await safeFetch(`${apiUrl}/professions/list`, {
      headers: typeof getAuthHeaders === 'function' ? getAuthHeaders() : getAuthHeaders
    });

    if (!result.success) {
      throw new Error(result.error || 'Ошибка загрузки списка профессий');
    }

    const data = result.data || {};
    if (!data.success || !data.professions) {
      throw new Error('Некорректный ответ сервера (professions)');
    }

    cache = data.professions;
    cacheTimestamp = now;
    return cache;
  }

  function getProfessionIcon(professionId) {
    const meta = getFromCache(professionId);
    if (meta && meta.icon) {
      return meta.icon;
    }
    return FALLBACK_ICONS[professionId] || '💼';
  }

  function getProfessionName(professionId) {
    const meta = getFromCache(professionId);
    if (meta && meta.name) {
      return meta.name;
    }
    return FALLBACK_NAMES[professionId] || professionId;
  }

  function getProfessionMeta(professionId) {
    if (!professionId) {
      return null;
    }
    const meta = getFromCache(professionId);
    if (meta) {
      return {
        id: professionId,
        name: meta.name,
        icon: meta.icon,
        stats: meta.stats || [],
        benefits: meta.benefits || []
      };
    }
    return {
      id: professionId,
      name: getProfessionName(professionId),
      icon: getProfessionIcon(professionId),
      stats: [],
      benefits: []
    };
  }

  function getProfessionStatLabel(statKey) {
    return STAT_LABELS[statKey] || statKey;
  }

  function renderProfessionBadge(professionId, level = 0, options = {}) {
    if (!professionId) {
      return '';
    }
    const meta = getProfessionMeta(professionId);
    if (!meta) {
      return '';
    }

    const icon = meta.icon || getProfessionIcon(professionId);
    const name = escapeHtml(meta.name || getProfessionName(professionId));
    const levelHtml = level ? `<span class="profession-level">ур. ${level}</span>` : '';
    const showBenefit = options.showBenefit !== false;
    const benefitsCount = options.benefitsCount ?? 1;

    let benefitsHtml = '';
    if (showBenefit && Array.isArray(meta.benefits) && meta.benefits.length > 0) {
      const benefits = meta.benefits.slice(0, benefitsCount).map(text => escapeHtml(text));
      benefitsHtml = `<div class="profession-bonus">${benefits.join(' • ')}</div>`;
    }

    return `
        <div class="profession-tag">
            <div class="profession-icon">${icon}</div>
            <div class="profession-info">
                <div class="profession-name">${name} ${levelHtml}</div>
                ${benefitsHtml}
            </div>
        </div>
    `;
  }

  function clearCache() {
    cache = null;
    cacheTimestamp = 0;
  }

  return {
    fetchProfessionsData,
    getProfessionMeta,
    getProfessionIcon,
    getProfessionName,
    getProfessionStatLabel,
    renderProfessionBadge,
    clearCache
  };
}
